/**
 * 範本報表生成器
 * Reports → 範本報表
 *
 * Step 1: 沿用 AppsMediaReports 篩選 + 勾選相片
 * Step 2: 選範本 + AI 配對（PHOTO slot ↔ 照片）
 * Step 3: 確認預覽 + 生成報表下載
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Steps, Button, Space, message, notification, Card, Typography, Tag, Badge,
  Spin, Row, Col, Select, Divider, Tooltip, Empty, Avatar,
  List, Image, Alert, Form, Input,
} from 'antd';
import {
  ArrowLeftOutlined, ArrowRightOutlined, ThunderboltOutlined,
  DownloadOutlined, PictureOutlined, LoadingOutlined, WarningOutlined,
  SearchOutlined, ReloadOutlined,
} from '@ant-design/icons';
import { useLanguage } from '../../../contexts/LanguageContext';
import InstanceFilters from '../AppsMediaReports/components/InstanceFilters';
import ProcessVariableFilters from '../AppsMediaReports/components/ProcessVariableFilters';
import MediaGallery from '../AppsMediaReports/components/MediaGallery';
import { groupMediaByExecutionAndPV, fetchExecutionEformInstances, fetchEformInstanceFullData } from '../AppsMediaReports/utils/mediaGrouping';
import { makeSubGroupKey } from '../AppsMediaReports/utils/filterExcludedMedia';
import { exportGalleryToZip } from '../AppsMediaReports/utils/zipExport';
import { reportTemplatesApi } from '../../../services/reportTemplatesApi';
import {
  loadTemplateReportQueryState,
  saveTemplateReportQueryState,
  loadTemplateReportSelection,
  saveTemplateReportSelection,
  loadTemplateReportExcluded,
  saveTemplateReportExcluded,
  loadTemplateReportGallerySettings,
  saveTemplateReportGallerySettings,
  normalizeMediaFilePath,
} from '../../../services/uiPreferenceApi';

const { Title, Text } = Typography;
const { Option } = Select;

const QUERY_SAVE_DEBOUNCE_MS = 1000;
const SELECTION_SAVE_DEBOUNCE_MS = 1000;

// ─── 主元件 ──────────────────────────────────────────────────────────────────

export default function TemplateReportBuilderPage() {
  const [currentStep, setCurrentStep] = useState(0);

  // Step 1 狀態 (提升至頂層以便 Step 2 讀取 + 持久化)
  const [groupedMedia, setGroupedMedia] = useState([]);
  const [selectedByGroup, setSelectedByGroup] = useState({});
  const [excludedByGroup, setExcludedByGroup] = useState({});
  const [pvDefinitions, setPvDefinitions] = useState([]);
  // 篩選條件 (提升到頂層做持久化)
  const [filters, setFilters] = useState({ status: 'all', workflowName: '', startDateRange: null, dateFilterType: 'eform' });
  const [pvFilters, setPvFilters] = useState([]);
  const [showPvFilters, setShowPvFilters] = useState(false);
  // 分組關鍵字（供 selectedPhotos + AI 使用）
  const [groupingPvs, setGroupingPvs] = useState([]);
  const [groupingEformFields, setGroupingEformFields] = useState([]);
  const [queryStateLoaded, setQueryStateLoaded] = useState(false);
  const querySaveTimerRef = useRef(null);
  const selectionSaveTimerRef = useRef(null);
  const excludedSaveTimerRef = useRef(null);
  const gallerySaveTimerRef = useRef(null);

  // 個人畫廊顯示設定（hideSelected / hideExcluded）
  // hideExcluded 預設 false：讓用戶看到全部圖片（均顯示「不列印」外觀），只需勾選要印的
  const [galleryDisplaySettings, setGalleryDisplaySettings] = useState({ hideSelected: true, hideExcluded: false });

  // 從 DB 載入查詢條件 + 畫廊顯示設定（mount 一次）
  useEffect(() => {
    Promise.all([
      loadTemplateReportQueryState(),
      loadTemplateReportGallerySettings(),
    ]).then(([qs, gs]) => {
      setFilters({ dateFilterType: 'eform', ...qs.filters });
      setPvFilters(qs.pvFilters);
      setGroupingPvs(qs.groupingPvs);
      setGroupingEformFields(qs.groupingEformFields);
      setShowPvFilters(qs.showPvFilters);
      setGalleryDisplaySettings(gs);
      setQueryStateLoaded(true);
    });
    return () => {
      if (querySaveTimerRef.current) clearTimeout(querySaveTimerRef.current);
      if (selectionSaveTimerRef.current) clearTimeout(selectionSaveTimerRef.current);
      if (excludedSaveTimerRef.current) clearTimeout(excludedSaveTimerRef.current);
      if (gallerySaveTimerRef.current) clearTimeout(gallerySaveTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 當 workflowName 改變時，從 DB 載入全公司已儲存的勾選設定 + 不列印標記
  useEffect(() => {
    const workflowName = filters.workflowName;
    if (!workflowName) {
      setSelectedByGroup({});
      setExcludedByGroup({});
      return;
    }
    let cancelled = false;
    Promise.all([
      loadTemplateReportSelection(workflowName),
      loadTemplateReportExcluded(workflowName),
    ]).then(([saved, excluded]) => {
      if (!cancelled) {
        setSelectedByGroup(saved);
        setExcludedByGroup(excluded);
      }
    }).catch(() => {
      if (!cancelled) { setSelectedByGroup({}); setExcludedByGroup({}); }
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.workflowName]);

  // 篩選條件改變時 debounced 存回 DB
  useEffect(() => {
    if (!queryStateLoaded) return;
    if (querySaveTimerRef.current) clearTimeout(querySaveTimerRef.current);
    querySaveTimerRef.current = setTimeout(() => {
      saveTemplateReportQueryState({ filters, pvFilters, groupingPvs, groupingEformFields, showPvFilters })
        .catch(err => console.error('[TemplateReportBuilder] 儲存查詢條件失敗:', err));
    }, QUERY_SAVE_DEBOUNCE_MS);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pvFilters, groupingPvs, groupingEformFields, showPvFilters, queryStateLoaded]);

  // 更新某 subGroup 的選取清單，並 debounced 存回 DB（全公司共享）
  const handleSelectionChange = useCallback((subGroupKey, orderedPaths) => {
    setSelectedByGroup(prev => {
      const next = { ...prev, [subGroupKey]: orderedPaths };
      if (selectionSaveTimerRef.current) clearTimeout(selectionSaveTimerRef.current);
      selectionSaveTimerRef.current = setTimeout(() => {
        const wf = filters.workflowName;
        if (wf) saveTemplateReportSelection(wf, next)
          .catch(err => console.error('[TemplateReportBuilder] 儲存勾選設定失敗:', err));
      }, SELECTION_SAVE_DEBOUNCE_MS);
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.workflowName]);

  // 更新某 subGroup 的「不列印」標記，並 debounced 存回 DB（全公司共享）
  const handleExcludedChange = useCallback((subGroupKey, newExcludedPaths) => {
    setExcludedByGroup(prev => {
      const next = { ...prev, [subGroupKey]: newExcludedPaths };
      if (excludedSaveTimerRef.current) clearTimeout(excludedSaveTimerRef.current);
      excludedSaveTimerRef.current = setTimeout(() => {
        const wf = filters.workflowName;
        if (wf) saveTemplateReportExcluded(wf, next)
          .catch(err => console.error('[TemplateReportBuilder] 儲存不列印標記失敗:', err));
      }, SELECTION_SAVE_DEBOUNCE_MS);
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.workflowName]);

  // 更新個人畫廊顯示設定，並 debounced 存回 DB
  const handleGalleryDisplayChange = useCallback((key, value) => {
    setGalleryDisplaySettings(prev => {
      const next = { ...prev, [key]: value };
      if (gallerySaveTimerRef.current) clearTimeout(gallerySaveTimerRef.current);
      gallerySaveTimerRef.current = setTimeout(() => {
        saveTemplateReportGallerySettings(next)
          .catch(err => console.error('[TemplateReportBuilder] 儲存畫廊設定失敗:', err));
      }, SELECTION_SAVE_DEBOUNCE_MS);
      return next;
    });
  }, []);

  // 查詢完成後從 DB 重新載入選取 + 不列印（確保拿到最新公司共享狀態）
  const reloadSelectionFromDB = useCallback(() => {
    const wf = filters.workflowName;
    if (!wf) {
      setSelectedByGroup({});
      setExcludedByGroup({});
      return;
    }
    Promise.all([
      loadTemplateReportSelection(wf),
      loadTemplateReportExcluded(wf),
    ]).then(([saved, excluded]) => {
      setSelectedByGroup(saved);
      setExcludedByGroup(excluded);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.workflowName]);

  // 從 groupedMedia + selectedByGroup 提取「已選照片」flat list，並附帶 pvContext。
  // 只有兩態（已選 / 未選），以 per-subGroupKey 查找避免 stale key 虛增計數。
  const selectedPhotos = React.useMemo(() => {
    const result = [];
    groupedMedia.forEach(group => {
      const pvParts = (groupingPvs || [])
        .map(name => group.processVariables?.[name])
        .filter(v => v != null && v !== 'null' && v !== '');
      const efParts = (groupingEformFields || [])
        .map(({ eformName, fieldId }) => group.eformValues?.[`${eformName}:${fieldId}`])
        .filter(v => v != null && v !== '');
      const pvContext = [...pvParts, ...efParts].join(' / ') || null;

      (group.subGroups || []).forEach(subGroup => {
        const subGroupKey = makeSubGroupKey(group.groupKey, subGroup.status);
        const selectedSet = new Set(selectedByGroup[subGroupKey] || []);
        if (selectedSet.size === 0) return;
        (subGroup.mediaFiles || []).forEach(file => {
          const normalized = normalizeMediaFilePath ? normalizeMediaFilePath(file.filePath) : file.filePath;
          if (selectedSet.has(file.filePath) || selectedSet.has(normalized)) {
            result.push({ ...file, pvContext });
          }
        });
      });
    });
    return result;
  }, [groupedMedia, selectedByGroup, groupingPvs, groupingEformFields]);

  // Step 2：選範本 + AI 配對
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templateDetail, setTemplateDetail] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [mapping, setMapping] = useState({});
  const [aiLoading, setAiLoading] = useState(false);

  // Step 3：生成
  const [generating, setGenerating] = useState(false);
  const [pvValues, setPvValues] = useState({});
  const [pvAutoFilling, setPvAutoFilling] = useState(false);

  // 當切換到 Step 3 且 templateDetail 有文字 Placeholder 時，
  // 自動從選取的 execution 的 eform instances 預填值（不覆蓋用戶已輸入的值）
  useEffect(() => {
    if (currentStep !== 2 || !templateDetail) return;
    const textPhs = (templateDetail.placeholders || []).filter(p => p.type !== 'PHOTO' && p.type !== 'SYS');
    if (textPhs.length === 0) return;

    // 收集有選取相片的 execution IDs（從 groupedMedia 中找對應 subGroup）
    const selectedGroupKeys = new Set(
      Object.keys(selectedByGroup).filter(k => (selectedByGroup[k] || []).length > 0)
    );
    const executionIds = new Set();
    groupedMedia.forEach(group => {
      (group.subGroups || []).forEach(subGroup => {
        // subGroupKey 是計算而來，不是 subGroup 的屬性
        const key = makeSubGroupKey(group.groupKey, subGroup.status);
        if (selectedGroupKeys.has(key)) {
          // executions 是陣列，取所有 execution IDs
          (subGroup.executions || []).forEach(ex => {
            if (ex?.id) executionIds.add(ex.id);
          });
          // 也嘗試 workflowExecution（最新代表）作 fallback
          if (subGroup.workflowExecution?.id) executionIds.add(subGroup.workflowExecution.id);
        }
      });
    });
    console.log('[TemplateReportBuilder] auto-fill: selectedGroupKeys=', [...selectedGroupKeys], 'executionIds=', [...executionIds]);
    if (executionIds.size === 0) {
      console.warn('[TemplateReportBuilder] auto-fill: no execution IDs found, skipping');
      return;
    }

    let cancelled = false;
    setPvAutoFilling(true);
    (async () => {
      try {
        // 取每個 execution 的 eform instances，再取完整 parsedFieldValues
        const allFieldValues = {}; // { fieldId: firstNonEmptyValue }
        const allPvValues = {};     // { fieldName: value } from processVariables

        // 收集 processVariables
        groupedMedia.forEach(group => {
          (group.subGroups || []).forEach(subGroup => {
            const sgKey = makeSubGroupKey(group.groupKey, subGroup.status);
            if (selectedGroupKeys.has(sgKey)) {
              Object.assign(allPvValues, group.processVariables || {});
            }
          });
        });

        // 收集已有的 eformValues（grouping 欄位）
        groupedMedia.forEach(group => {
          (group.subGroups || []).forEach(subGroup => {
            const sgKey = makeSubGroupKey(group.groupKey, subGroup.status);
            if (selectedGroupKeys.has(sgKey)) {
              Object.entries(group.eformValues || {}).forEach(([k, v]) => {
                if (!v) return;
                const fieldId = k.includes(':') ? k.split(':').slice(1).join(':') : k;
                if (!allFieldValues[fieldId]) allFieldValues[fieldId] = String(v);
              });
            }
          });
        });

        // 從 execution 的 eform instances 取完整欄位值（含非分組欄位）
        await Promise.all(Array.from(executionIds).map(async (eid) => {
          try {
            const instances = await fetchExecutionEformInstances(eid);
            console.log(`[TemplateReportBuilder] auto-fill: execution ${eid} → ${instances?.length ?? 0} eform instances`);
            if (!instances || instances.length === 0) return;
            await Promise.all(instances.map(async (inst) => {
              try {
                const full = await fetchEformInstanceFullData(inst.id);
                console.log(`[TemplateReportBuilder] auto-fill: eform instance ${inst.id} parsedFieldValues=`, full?.parsedFieldValues);
                if (!full?.parsedFieldValues) return;
                Object.entries(full.parsedFieldValues).forEach(([fieldId, val]) => {
                  const strVal = val != null ? String(val).trim() : '';
                  if (strVal && !allFieldValues[fieldId]) {
                    allFieldValues[fieldId] = strVal;
                  }
                });
              } catch (e) {
                console.warn(`[TemplateReportBuilder] auto-fill: failed to fetch eform instance ${inst.id}`, e);
              }
            }));
          } catch (e) {
            console.warn(`[TemplateReportBuilder] auto-fill: failed to fetch eform instances for execution ${eid}`, e);
          }
        }));

        if (cancelled) return;
        console.log('[TemplateReportBuilder] auto-fill: allFieldValues=', allFieldValues, 'allPvValues=', allPvValues);

        // 預填 pvValues（不覆蓋用戶已手動輸入的值）
        setPvValues(prev => {
          const next = { ...prev };
          let changed = false;
          textPhs.forEach(ph => {
            const key = `${ph.type}:${ph.key}`;
            if (next[key]) return; // 已有值，不覆蓋
            let val = '';
            if (ph.type === 'EFORM') {
              val = allFieldValues[ph.key] || '';
            } else if (ph.type === 'PV') {
              val = allPvValues[ph.key] != null ? String(allPvValues[ph.key]) : '';
            }
            if (val) { next[key] = val; changed = true; }
          });
          return changed ? next : prev;
        });
      } finally {
        if (!cancelled) setPvAutoFilling(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, templateDetail]);

  useEffect(() => {
    reportTemplatesApi.list()
      .then(list => setTemplates([...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''))))
      .catch(() => {});
  }, []);

  // ─── Step 2：選範本 & AI 配對 ────────────────────────────────────────────

  const handleTemplateChange = async (id) => {
    setSelectedTemplateId(id);
    setMapping({});
    if (!id) { setTemplateDetail(null); return; }
    setTemplateLoading(true);
    try {
      const detail = await reportTemplatesApi.get(id);
      setTemplateDetail(detail);
      const m = {};
      (detail.placeholders || [])
        .filter(p => p.type === 'PHOTO')
        .forEach(p => { m[p.slotLabel || p.key] = []; });
      setMapping(m);
    } catch {
      message.error('載入範本詳情失敗');
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleAiSuggest = async () => {
    if (!selectedTemplateId || selectedPhotos.length === 0) return;
    setAiLoading(true);
    try {
      const photos = selectedPhotos.map(p => ({
        filePath: p.filePath || '',
        caption: p.caption || p.description || p.shortPhrase || '',
        // pvContext is the primary matching clue (e.g. "T1天台食水")
        pvData: p.pvContext || p.pvLabel || '',
      }));
      // Collect unique pvContext values as context keywords for the AI prompt
      const contextKeywords = [...new Set(selectedPhotos.map(p => p.pvContext).filter(Boolean))];
      const { suggestions } = await reportTemplatesApi.suggestMapping(selectedTemplateId, photos, contextKeywords);
      const newMapping = {};
      Object.keys(mapping).forEach(k => { newMapping[k] = []; });
      suggestions.forEach(s => {
        if (s.suggestedSlot && (s.confidence === 'high' || s.confidence === 'medium')) {
          if (newMapping[s.suggestedSlot] !== undefined) {
            newMapping[s.suggestedSlot] = [...(newMapping[s.suggestedSlot] || []), s.filePath];
          }
        }
      });
      setMapping(newMapping);
      message.success('AI 配對完成，請確認並手動調整');
    } catch (e) {
      message.error('AI 配對失敗：' + (e?.message || e));
    } finally {
      setAiLoading(false);
    }
  };

  const handleTogglePhotoInSlot = useCallback((slotLabel, filePath) => {
    setMapping(prev => {
      const current = prev[slotLabel] || [];
      const exists = current.includes(filePath);
      return {
        ...prev,
        [slotLabel]: exists ? current.filter(p => p !== filePath) : [...current, filePath],
      };
    });
  }, []);

  // ─── Step 3：生成報表 ─────────────────────────────────────────────────────

  const handleGenerate = async () => {
    if (!selectedTemplateId) return;
    setGenerating(true);
    try {
      const photoMapping = Object.entries(mapping)
        .filter(([, photos]) => photos.length > 0)
        .map(([slotLabel, photos]) => ({ slotLabel, photos }));

      // pvValues dict uses "TYPE:key" as key — split into separate pvValues / eformValues for backend
      const pvValuesForApi = {};
      const eformValuesForApi = {};
      Object.entries(pvValues).forEach(([typeKey, val]) => {
        if (typeKey.startsWith('PV:')) pvValuesForApi[typeKey.slice(3)] = val;
        else if (typeKey.startsWith('EFORM:')) eformValuesForApi[typeKey.slice(6)] = val;
        else pvValuesForApi[typeKey] = val; // backward compat
      });
      console.log('[TemplateReportBuilder] generate: pvValues state=', pvValues);
      console.log('[TemplateReportBuilder] generate: pvValuesForApi=', pvValuesForApi, 'eformValuesForApi=', eformValuesForApi);

      const { blob, filename } = await reportTemplatesApi.generate(selectedTemplateId, {
        photoMapping,
        pvValues: pvValuesForApi,
        eformValues: eformValuesForApi,
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      message.success('報表已生成並開始下載！');
    } catch (e) {
      const detail = e?.message || String(e);
      console.error('[TemplateReportBuilder] generate error:', e);
      notification.error({
        message: '生成失敗',
        description: detail || '未知錯誤，請查看瀏覽器 Console',
        duration: 0,    // stay until user closes
        style: { maxWidth: 520 },
      });
    } finally {
      setGenerating(false);
    }
  };

  const steps = [
    { title: '選照片', description: '篩選並勾選要納入報表的照片' },
    { title: '配對範本', description: '選擇範本並指定照片槽位' },
    { title: '生成報表', description: '確認設定後下載報表檔案' },
  ];

  const canNext1 = selectedPhotos.length > 0;
  const canNext2 = selectedTemplateId != null;

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginBottom: 4 }}>範本報表</Title>
      <Text type="secondary">以 Office 範本生成圖文報表（支援 Word / Excel / PowerPoint）</Text>
      <Divider />

      <Steps current={currentStep} items={steps} style={{ marginBottom: 24 }} size="small" />

      {currentStep === 0 && (
        <Step1PhotoSelection
          groupedMedia={groupedMedia}
          setGroupedMedia={setGroupedMedia}
          selectedByGroup={selectedByGroup}
          setSelectedByGroup={setSelectedByGroup}
          onSelectionChange={handleSelectionChange}
          excludedByGroup={excludedByGroup}
          setExcludedByGroup={setExcludedByGroup}
          onExcludedChange={handleExcludedChange}
          galleryDisplaySettings={galleryDisplaySettings}
          onGalleryDisplayChange={handleGalleryDisplayChange}
          onReloadSelection={reloadSelectionFromDB}
          pvDefinitions={pvDefinitions}
          setPvDefinitions={setPvDefinitions}
          filters={filters}
          setFilters={setFilters}
          pvFilters={pvFilters}
          setPvFilters={setPvFilters}
          showPvFilters={showPvFilters}
          setShowPvFilters={setShowPvFilters}
          groupingPvs={groupingPvs}
          setGroupingPvs={setGroupingPvs}
          groupingEformFields={groupingEformFields}
          setGroupingEformFields={setGroupingEformFields}
        />
      )}

      {currentStep === 1 && (
        <Step2Mapping
          selectedPhotos={selectedPhotos}
          templates={templates}
          selectedTemplateId={selectedTemplateId}
          onTemplateChange={handleTemplateChange}
          templateDetail={templateDetail}
          templateLoading={templateLoading}
          mapping={mapping}
          onTogglePhoto={handleTogglePhotoInSlot}
          onAiSuggest={handleAiSuggest}
          aiLoading={aiLoading}
          groupingPvs={groupingPvs}
          groupingEformFields={groupingEformFields}
        />
      )}

      {currentStep === 2 && (
        <Step3Confirm
          templateDetail={templateDetail}
          mapping={mapping}
          selectedPhotos={selectedPhotos}
          pvValues={pvValues}
          onPvValuesChange={setPvValues}
          pvAutoFilling={pvAutoFilling}
        />
      )}

      <Divider />
      <Space>
        {currentStep > 0 && (
          <Button icon={<ArrowLeftOutlined />} onClick={() => setCurrentStep(s => s - 1)}>
            上一步
          </Button>
        )}
        {currentStep < 2 && (
          <Button
            type="primary"
            icon={<ArrowRightOutlined />}
            disabled={currentStep === 0 ? !canNext1 : !canNext2}
            onClick={() => setCurrentStep(s => s + 1)}
          >
            下一步
            {currentStep === 0 && selectedPhotos.length > 0 && ` (已選 ${selectedPhotos.length} 張)`}
          </Button>
        )}
        {currentStep === 2 && (
          <Button
            type="primary"
            icon={generating ? <LoadingOutlined /> : <DownloadOutlined />}
            loading={generating}
            onClick={handleGenerate}
            disabled={!selectedTemplateId}
          >
            生成並下載報表
          </Button>
        )}
      </Space>
    </div>
  );
}

// ─── Step 1：照片選擇（嵌入 AppsMediaReports 查詢邏輯）──────────────────────

function Step1PhotoSelection({
  groupedMedia, setGroupedMedia,
  selectedByGroup, setSelectedByGroup, onSelectionChange,
  excludedByGroup, setExcludedByGroup, onExcludedChange,
  galleryDisplaySettings = { hideSelected: true, hideExcluded: true },
  onGalleryDisplayChange,
  onReloadSelection,
  pvDefinitions, setPvDefinitions,
  filters, setFilters,
  pvFilters, setPvFilters,
  showPvFilters, setShowPvFilters,
  groupingPvs, setGroupingPvs,
  groupingEformFields, setGroupingEformFields,
}) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [zipSelectedLoading, setZipSelectedLoading] = useState(false);
  const [zipAllLoading, setZipAllLoading] = useState(false);

  // 只計算屬於當前 groupedMedia subGroupKey 的已選數，避免 stale key 虛增
  const totalSelected = React.useMemo(() => {
    const validKeys = new Set(
      groupedMedia.flatMap(g =>
        (g.subGroups || []).map(sg => makeSubGroupKey(g.groupKey, sg.status))
      )
    );
    return Object.entries(selectedByGroup)
      .filter(([key]) => validKeys.has(key))
      .reduce((sum, [, paths]) => sum + paths.length, 0);
  }, [groupedMedia, selectedByGroup]);

  // 所有收到的相片總數
  const totalAll = React.useMemo(() =>
    groupedMedia.reduce((sum, g) =>
      sum + (g.subGroups || []).reduce((s, sg) => s + (sg.mediaFiles?.length || 0), 0), 0),
    [groupedMedia]
  );

  const handleZipDownloadSelected = async () => {
    if (!groupedMedia || groupedMedia.length === 0) {
      message.warning('請先查詢取得相片資料');
      return;
    }
    if (totalSelected === 0) {
      message.warning('請先選取相片後再下載 ZIP');
      return;
    }
    setZipSelectedLoading(true);
    try {
      await exportGalleryToZip(
        groupedMedia,
        {},
        filters.workflowName || 'media_export',
        null,
        groupingPvs,
        selectedByGroup,
        pvDefinitions,
        groupingEformFields
      );
      message.success('ZIP 已開始下載（選中的相片）');
    } catch (e) {
      message.error('ZIP 下載失敗：' + (e?.message || e));
    } finally {
      setZipSelectedLoading(false);
    }
  };

  const handleZipDownloadAll = async () => {
    if (!groupedMedia || groupedMedia.length === 0) {
      message.warning('請先查詢取得相片資料');
      return;
    }
    setZipAllLoading(true);
    try {
      await exportGalleryToZip(
        groupedMedia,
        {},
        filters.workflowName || 'media_export',
        null,
        groupingPvs,
        null,
        pvDefinitions,
        groupingEformFields
      );
      message.success('ZIP 已開始下載（所有收到的相片）');
    } catch (e) {
      message.error('ZIP 下載失敗：' + (e?.message || e));
    } finally {
      setZipAllLoading(false);
    }
  };

  const loadMedia = async () => {
    if (!filters.workflowName && filters.status === 'all' && !filters.startDateRange) {
      message.warning('請先設置過濾條件（至少選擇工作流程名稱）');
      return;
    }
    setLoading(true);
    try {
      const grouped = await groupMediaByExecutionAndPV(
        filters, pvFilters, groupingPvs, () => {}, groupingEformFields
      );
      setGroupedMedia(grouped);
      // 查詢完成後從 DB 重新載入選取 + 不列印（不直接清空，保留已標記的狀態）
      if (onReloadSelection) {
        onReloadSelection();
      }
      if (grouped.length === 0) {
        message.info('沒有找到符合條件的媒體文件');
      } else {
        const totalImages = grouped.reduce((sum, g) =>
          sum + (g.subGroups || []).reduce((s, sg) => s + (sg.mediaFiles?.length || 0), 0), 0);
        message.success(`找到 ${grouped.length} 個群組，共 ${totalImages} 張圖片`);
      }
    } catch (e) {
      message.error('載入失敗：' + (e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  // 使用來自父層帶 DB 存儲的 handlers（若未傳入則 fallback 到 setter）
  const handleSelectionChange = useCallback((subGroupKey, orderedPaths) => {
    if (onSelectionChange) {
      onSelectionChange(subGroupKey, orderedPaths);
    } else {
      setSelectedByGroup(prev => ({ ...prev, [subGroupKey]: orderedPaths }));
    }
  }, [onSelectionChange, setSelectedByGroup]);

  const handleExcludedChange = useCallback((subGroupKey, newExcludedPaths) => {
    if (onExcludedChange) {
      onExcludedChange(subGroupKey, newExcludedPaths);
    } else {
      setExcludedByGroup(prev => ({ ...prev, [subGroupKey]: newExcludedPaths }));
    }
  }, [onExcludedChange, setExcludedByGroup]);

  return (
    <div>
      {totalSelected > 0 && (
        <Alert
          type="success"
          showIcon
          icon={<PictureOutlined />}
          message={`已勾選 ${totalSelected} 張照片。確認後按「下一步」選擇範本。`}
          style={{ marginBottom: 12 }}
        />
      )}
      <Card size="small" style={{ marginBottom: 12 }}>
        <InstanceFilters
          filters={filters}
          onFiltersChange={setFilters}
          onSearch={loadMedia}
          onReset={() => { setFilters(prev => ({ ...prev, status: 'all', workflowName: '', startDateRange: null })); setGroupedMedia([]); setSelectedByGroup({}); }}
          loading={loading}
          dateFilterType={filters.dateFilterType || 'eform'}
          onDateFilterTypeChange={(type) => setFilters(prev => ({ ...prev, dateFilterType: type }))}
          extraButtons={
            <Space size={4}>
              <Button
                icon={zipSelectedLoading ? <LoadingOutlined /> : <DownloadOutlined />}
                loading={zipSelectedLoading}
                onClick={handleZipDownloadSelected}
                disabled={groupedMedia.length === 0 || totalSelected === 0}
                size="middle"
                title={totalSelected > 0 ? `下載已選 ${totalSelected} 張相片的 ZIP` : '請先查詢並選取相片'}
              >
                下載選中的{totalSelected > 0 ? ` (${totalSelected})` : ''}
              </Button>
              <Button
                icon={zipAllLoading ? <LoadingOutlined /> : <DownloadOutlined />}
                loading={zipAllLoading}
                onClick={handleZipDownloadAll}
                disabled={groupedMedia.length === 0}
                size="middle"
                title={totalAll > 0 ? `下載所有 ${totalAll} 張收到的相片的 ZIP` : '請先查詢相片'}
              >
                所有收到的{totalAll > 0 ? ` (${totalAll})` : ''}
              </Button>
            </Space>
          }
        />
      </Card>

      <Card
        size="small"
        style={{ marginBottom: 12 }}
        extra={
          <Button
            type="text"
            size="small"
            onClick={() => setShowPvFilters(v => !v)}
          >
            {showPvFilters ? '收合' : '展開'} PV 分組
          </Button>
        }
        title="流程資料過濾（用於分組）"
      >
        <div style={{ display: showPvFilters ? 'block' : 'none' }}>
          <ProcessVariableFilters
            selectedWorkflowName={filters.workflowName}
            selectedPvFilters={pvFilters}
            onPvFiltersChange={setPvFilters}
            selectedGroupingPvs={groupingPvs}
            onGroupingPvsChange={setGroupingPvs}
            selectedGroupingEformFields={groupingEformFields}
            onGroupingEformFieldsChange={setGroupingEformFields}
            onProcessVariablesLoaded={setPvDefinitions}
          />
        </div>
      </Card>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin tip="載入中..." /></div>
      ) : (
        <MediaGallery
          groupedMedia={groupedMedia}
          settings={{ imageGap: 5, minImageWidth: 50 }}
          groupingPvs={groupingPvs}
          pvDefinitions={pvDefinitions}
          groupingEformFields={groupingEformFields}
          selectedByGroup={selectedByGroup}
          onSelectionChange={handleSelectionChange}
          excludedByGroup={excludedByGroup}
          onExcludedChange={handleExcludedChange}
          hideSelected={galleryDisplaySettings.hideSelected ?? true}
          hideExcluded={galleryDisplaySettings.hideExcluded ?? true}
          onHideSelectedChange={v => onGalleryDisplayChange && onGalleryDisplayChange('hideSelected', v)}
          onHideExcludedChange={v => onGalleryDisplayChange && onGalleryDisplayChange('hideExcluded', v)}
        />
      )}
    </div>
  );
}

// ─── Step 2：配對 ─────────────────────────────────────────────────────────────

function Step2Mapping({
  selectedPhotos, templates, selectedTemplateId, onTemplateChange,
  templateDetail, templateLoading, mapping, onTogglePhoto, onAiSuggest, aiLoading,
  groupingPvs, groupingEformFields,
}) {
  const photoSlots = (templateDetail?.placeholders || []).filter(p => p.type === 'PHOTO');

  const assignedPaths = new Set(Object.values(mapping).flat());
  const unassignedCount = selectedPhotos.filter(p => !assignedPaths.has(p.filePath)).length;

  // Unique pvContext values present among selected photos
  const pvContextSet = [...new Set(selectedPhotos.map(p => p.pvContext).filter(Boolean))];

  // Labels for grouping PVs / eForm fields used as keywords
  const keywordLabels = [
    ...(groupingPvs || []),
    ...((groupingEformFields || []).map(f => f.displayLabel || f.fieldId)),
  ];

  return (
    <Row gutter={16}>
      {/* 左欄：PHOTO 槽位 */}
      <Col xs={24} md={12}>
        <Card
          size="small"
          title="相片槽位（PHOTO Placeholder）"
          extra={
            <Space wrap>
              <Select
                placeholder="選擇範本"
                value={selectedTemplateId}
                onChange={onTemplateChange}
                style={{ width: 200 }}
                loading={templateLoading}
              >
                {templates.map(t => (
                  <Option key={t.id} value={t.id}>
                    <Tag
                      color={t.fileType === 'docx' ? 'blue' : t.fileType === 'xlsx' ? 'green' : 'orange'}
                      style={{ marginRight: 4 }}
                    >
                      {t.fileType}
                    </Tag>
                    {t.name}
                  </Option>
                ))}
              </Select>
              {selectedTemplateId && (
                <Button
                  size="small"
                  type="primary"
                  icon={aiLoading ? <LoadingOutlined /> : <ThunderboltOutlined />}
                  loading={aiLoading}
                  onClick={onAiSuggest}
                >
                  AI 推薦配對
                </Button>
              )}
            </Space>
          }
        >
          {templateLoading ? (
            <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
          ) : !selectedTemplateId ? (
            <Empty description="請先選擇範本" />
          ) : photoSlots.length === 0 ? (
            <Empty description="此範本沒有 PHOTO Placeholder" />
          ) : (
            photoSlots.map(slot => {
              const slotLabel = slot.slotLabel || slot.key;
              return (
                <SlotCard
                  key={slotLabel}
                  slotLabel={slotLabel}
                  slotOptions={slot.options || {}}
                  assignedPaths={mapping[slotLabel] || []}
                  allPhotos={selectedPhotos}
                  onToggle={(fp) => onTogglePhoto(slotLabel, fp)}
                />
              );
            })
          )}
        </Card>
      </Col>

      {/* 右欄：待配照片 */}
      <Col xs={24} md={12}>
        <Card
          size="small"
          title={`待配照片（未配對 ${unassignedCount}／共 ${selectedPhotos.length} 張）`}
          extra={
            pvContextSet.length > 0 && (
              <Space size={4} wrap>
                <Text type="secondary" style={{ fontSize: 11 }}>分組關鍵字：</Text>
                {pvContextSet.map(kw => (
                  <Tag key={kw} color="blue" style={{ fontSize: 11, margin: 0 }}>{kw}</Tag>
                ))}
              </Space>
            )
          }
        >
          {keywordLabels.length > 0 && (
            <div style={{ marginBottom: 8, padding: '4px 8px', background: '#f0f5ff', borderRadius: 4, fontSize: 11 }}>
              <Text type="secondary">AI 配對將以 </Text>
              {keywordLabels.map(k => <Tag key={k} color="geekblue" style={{ fontSize: 10, margin: '0 2px' }}>{k}</Tag>)}
              <Text type="secondary"> 欄位值作為主要配對依據</Text>
            </div>
          )}
          {selectedPhotos.length === 0 ? (
            <Empty description="未選擇照片，請返回 Step 1" />
          ) : (
            <List
              dataSource={selectedPhotos}
              renderItem={(photo) => {
                const fp = photo.filePath || '';
                const assignedSlot = Object.entries(mapping).find(([, arr]) => arr.includes(fp))?.[0];
                const thumb = getImageUrl(fp);
                return (
                  <List.Item style={{ padding: '6px 0' }}>
                    <Space align="start" style={{ width: '100%' }}>
                      <div style={{ width: 48, height: 48, overflow: 'hidden', borderRadius: 4, background: '#f5f5f5', flexShrink: 0 }}>
                        <Image
                          src={thumb}
                          width={48}
                          height={48}
                          style={{ objectFit: 'cover' }}
                          preview={false}
                          fallback={PLACEHOLDER_IMG}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text ellipsis style={{ fontSize: 12 }}>
                          {photo.caption || photo.shortPhrase || fp.split('/').pop()}
                        </Text>
                        <br />
                        <Space size={2} wrap>
                          {photo.pvContext && (
                            <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>
                              {photo.pvContext}
                            </Tag>
                          )}
                          {assignedSlot ? (
                            <Tag color="green" style={{ fontSize: 10, margin: 0 }}>→ {assignedSlot}</Tag>
                          ) : (
                            <Tag color="default" style={{ fontSize: 10, margin: 0 }}>未配對</Tag>
                          )}
                        </Space>
                      </div>
                    </Space>
                  </List.Item>
                );
              }}
              style={{ maxHeight: 500, overflowY: 'auto' }}
            />
          )}
        </Card>
      </Col>
    </Row>
  );
}

// 單個槽位卡片（允許勾選/取消相片）
function SlotCard({ slotLabel, slotOptions, assignedPaths, allPhotos, onToggle }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <Card
      size="small"
      style={{ marginBottom: 8 }}
      styles={{ body: { padding: '8px 12px' } }}
      title={
        <Space>
          <Tag color="orange">{slotLabel}</Tag>
          {slotOptions.cols && <Tag>{slotOptions.cols} 欄</Tag>}
          {slotOptions.max && <Tag>最多 {slotOptions.max}</Tag>}
          <Badge
            count={assignedPaths.length}
            showZero
            color={assignedPaths.length > 0 ? '#52c41a' : '#d9d9d9'}
          />
        </Space>
      }
      extra={
        <Button size="small" type="text" onClick={() => setExpanded(e => !e)}>
          {expanded ? '收合' : '展開'}
        </Button>
      }
    >
      {expanded && (
        <>
          {/* 已分配縮圖 */}
          {assignedPaths.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
              {assignedPaths.map(fp => (
                <div key={fp} style={{ position: 'relative' }}>
                  <Image
                    src={getImageUrl(fp)}
                    width={48}
                    height={48}
                    style={{ objectFit: 'cover', borderRadius: 4 }}
                    preview={false}
                    fallback={PLACEHOLDER_IMG}
                  />
                  <Button
                    size="small"
                    danger
                    type="text"
                    style={{ position: 'absolute', top: 0, right: 0, padding: 0, lineHeight: 1, minWidth: 16, height: 16, fontSize: 10 }}
                    onClick={() => onToggle(fp)}
                  >
                    ✕
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* 多選下拉 */}
          <Select
            mode="multiple"
            placeholder="+ 加入照片至此槽位"
            value={assignedPaths}
            onChange={(vals) => {
              const toAdd = vals.filter(v => !assignedPaths.includes(v));
              const toRemove = assignedPaths.filter(v => !vals.includes(v));
              toAdd.forEach(fp => onToggle(fp));
              toRemove.forEach(fp => onToggle(fp));
            }}
            style={{ width: '100%' }}
            size="small"
            optionLabelProp="label"
          >
          {allPhotos.map(p => {
              const fp = p.filePath || '';
              const name = p.caption || p.shortPhrase || fp.split('/').pop();
              return (
                <Option key={fp} value={fp} label={name}>
                  <Space size={4} align="center">
                    <Avatar
                      shape="square"
                      size={24}
                      src={getImageUrl(fp)}
                      icon={<PictureOutlined />}
                    />
                    <Text style={{ fontSize: 12 }}>{name}</Text>
                    {p.pvContext && (
                      <Tag color="blue" style={{ fontSize: 10, margin: 0, lineHeight: '18px' }}>
                        {p.pvContext}
                      </Tag>
                    )}
                  </Space>
                </Option>
              );
            })}
          </Select>
        </>
      )}
    </Card>
  );
}

// ─── Step 3：確認 & 生成 ─────────────────────────────────────────────────────

function Step3Confirm({ templateDetail, mapping, selectedPhotos, pvValues, onPvValuesChange, pvAutoFilling }) {
  const photoSlots = (templateDetail?.placeholders || []).filter(p => p.type === 'PHOTO');
  const textPhs = (templateDetail?.placeholders || []).filter(p => p.type !== 'PHOTO');
  const photoMap = Object.fromEntries(selectedPhotos.map(p => [p.filePath || '', p]));

  const totalMapped = Object.values(mapping).reduce((s, a) => s + a.length, 0);
  const unmappedSlots = photoSlots.filter(s => !(mapping[s.slotLabel || s.key]?.length > 0));

  return (
    <div>
      {unmappedSlots.length > 0 && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message={`${unmappedSlots.length} 個 PHOTO 槽位尚未配對（${unmappedSlots.map(s => s.slotLabel || s.key).join('、')}），生成時該位置留空。`}
          style={{ marginBottom: 16 }}
        />
      )}

      <Row gutter={16}>
        {/* 文字 Placeholder 填值 */}
        {textPhs.length > 0 && (
          <Col xs={24} md={12}>
            <Card
              size="small"
              title="文字 Placeholder 填值"
              style={{ marginBottom: 16 }}
              extra={pvAutoFilling ? <Spin size="small" tip="自動帶入中…" /> : null}
            >
              {textPhs.map((ph, idx) => (
                <Form.Item
                  key={idx}
                  label={
                    <Tooltip title={ph.raw}>
                      <Text style={{ fontSize: 12 }}>{ph.key}</Text>
                    </Tooltip>
                  }
                  style={{ marginBottom: 8 }}
                >
                  <Input
                    size="small"
                    placeholder={ph.type === 'SYS' ? '（系統自動填入）' : `輸入 ${ph.type} 值`}
                    disabled={ph.type === 'SYS'}
                    value={pvValues[`${ph.type}:${ph.key}`] || ''}
                    onChange={e => onPvValuesChange(prev => ({
                      ...prev,
                      [`${ph.type}:${ph.key}`]: e.target.value,
                    }))}
                  />
                </Form.Item>
              ))}
              <Text type="secondary" style={{ fontSize: 11 }}>
                SYS:DATE / SYS:TIME / SYS:OPERATOR 等系統值將在生成時自動填入
              </Text>
            </Card>
          </Col>
        )}

        {/* 照片配對預覽 */}
        <Col xs={24} md={textPhs.length > 0 ? 12 : 24}>
          <Card size="small" title={`照片配對預覽（共 ${totalMapped} 張已分配）`}>
            {photoSlots.length === 0 ? (
              <Text type="secondary">此範本無 PHOTO Placeholder</Text>
            ) : (
              photoSlots.map(slot => {
                const slotLabel = slot.slotLabel || slot.key;
                const paths = mapping[slotLabel] || [];
                return (
                  <div key={slotLabel} style={{ marginBottom: 16 }}>
                    <Space style={{ marginBottom: 6 }}>
                      <Tag color="orange">{slotLabel}</Tag>
                      {paths.length === 0
                        ? <Tag color="default">未配對</Tag>
                        : <Tag color="green">{paths.length} 張</Tag>}
                    </Space>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {paths.map(fp => (
                        <Image
                          key={fp}
                          src={getImageUrl(fp)}
                          width={52}
                          height={52}
                          style={{ objectFit: 'cover', borderRadius: 4 }}
                          preview={false}
                          fallback={PLACEHOLDER_IMG}
                        />
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}

// ─── 工具函式 ─────────────────────────────────────────────────────────────────

const getImageUrl = (filePath) => {
  if (!filePath) return '';
  if (filePath.startsWith('http') || filePath.startsWith('data:') || filePath.startsWith('/')) return filePath;
  return `/Uploads/${filePath}`;
};

const PLACEHOLDER_IMG = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTYiIGhlaWdodD0iNTYiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjU2IiBoZWlnaHQ9IjU2IiBmaWxsPSIjZThlOGU4Ii8+PC9zdmc+';
